import React, { useCallback, FC, ReactElement } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, ArrowRight, Pencil, GraphUp, Kanban } from 'react-bootstrap-icons';
import { Paper, Button } from '@mui/material';

import { reduxActions, useDispatch, useSelector } from 'store';
import { useAppNavigate } from 'hooks';
import { Link } from 'components/controls';
import { WiseAvatar } from 'components/wise';
import {
  CalendarPlusIcon,
  CalendarIcon,
  ClientsIcon,
  ClientsCartsIcon,
  OfferIcon,
  SalesIcon,
  DashboardIcon
} from 'assets/icons';

import styles from 'theme/components/containers/TraderClientDashboardMenu/TraderClientDashboardMenu.module.scss';

interface IMenuItem {
  label: string;
  icon?: ReactElement | null;
  path: string;
}

export interface ITraderClientDashboardMenuProps {
  isCondensed?: boolean;
  onToggleCondensed?: () => void;
}

const TraderClientDashboardMenu: FC<ITraderClientDashboardMenuProps> = ({
  isCondensed = false,
  onToggleCondensed
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const appNavigate = useAppNavigate();
  const { t, i18n } = useTranslation();
  const { pathname } = useLocation();

  const { currentClientId, currentClient: clientData } = useSelector((state) => state.traderDashboard);
  const { id: clientIdFromParams } = useParams<{ id: string }>();
  const clientId = currentClientId ?? (clientIdFromParams ? Number(clientIdFromParams) : null);
  const basePathClientId = clientId !== null ? String(clientId) : clientIdFromParams ?? '';

  const basePath = `/trader/clients/${basePathClientId}`;

  const menuItems: IMenuItem[] = [
    {
      label: 'Dashboard',
      icon: <DashboardIcon />,
      path: basePath
    },
    {
      label: 'Karta klienta',
      icon: <Pencil />,
      path: `${basePath}/edit`
    },
    {
      label: 'Zamówienia',
      icon: <SalesIcon />,
      path: `${basePath}/orders`
    },
    {
      label: 'Koszyki klienta',
      icon: <ClientsIcon />,
      path: `${basePath}/carts`
    },
    {
      label: 'Płatności',
      icon: <OfferIcon />,
      path: `${basePath}/payments`
    },
    {
      label: 'Oferty',
      icon: <ClientsCartsIcon />,
      path: `${basePath}/offers`
    },
    {
      label: 'Transakcje',
      icon: <Kanban size={20} />,
      path: `${basePath}/deals`
    },
    {
      label: 'Dokumenty',
      icon: <CalendarPlusIcon />,
      path: `${basePath}/documents`
    },
    {
      label: 'Listy zakupowe',
      icon: <CalendarPlusIcon />,
      path: `${basePath}/shopping-lists`
    },
    {
      label: 'Terminarz',
      icon: <CalendarIcon />,
      path: `${basePath}/calendar`
    },
    {
      label: 'Poznaj klienta',
      icon: <GraphUp size={20} />,
      path: `/trader/insights/${basePathClientId}`
    }
  ];

  const isCurrentPath = useCallback(
    (testedPath?: string) => {
      if (!testedPath) return false;
      if (testedPath === '/trader/clients') {
        return new RegExp(`^/[^/]+/trader/clients$`).test(pathname);
      }
      const escaped = testedPath.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      return new RegExp(`^/[^/]+${escaped}$`).test(pathname);
    },
    [pathname]
  );

  const handleCancelClient = () => {
    dispatch(reduxActions.setCurrentClient({ clientId: null, client: null }));
    navigate(`/${i18n.language}/trader`);
  };

  return (
    <Paper
      className={classnames(
        styles.componentWrapper,
        'StylePath-Containers-TraderClientDashboardMenu',
        { [styles.condensed]: isCondensed }
      )}>
      {clientData && (
        <WiseAvatar
          withDetails={!isCondensed}
          userData={{
            first_name: clientData.first_name,
            last_name: clientData.last_name,
            email: clientData.email
          }}
          onCancel={isCondensed ? undefined : handleCancelClient}
          className={classnames({
            [styles.clientAvatar]: !isCondensed,
            [styles.avatarCondensed]: isCondensed
          })}
        />
      )}
      <div className={styles.menuItems}>
        {menuItems.map((item, index) => {
          const isActive = isCurrentPath(item.path);
          return (
            <div className={styles.menuItem} key={index}>
              <Link
                to={item.path}
                onClick={() => dispatch(reduxActions.setIsMobileMenu(false))}
                className={classnames(styles.link, {
                  [styles.active]: isActive,
                  [styles.condensed]: isCondensed
                })}>
                {item.icon && <div className={styles.iconWrapper}>{item.icon}</div>}
                {!isCondensed && t(item.label)}
              </Link>
            </div>
          );
        })}
      </div>
      <div className={classnames(styles.bottomSection, {[styles.bottomSectionCondensed]: isCondensed})}>
        <WiseAvatar
          withDetails={!isCondensed}
          onClick={() => {
            dispatch(reduxActions.setIsMobileMenu(false));
            appNavigate('/trader');
          }}
          className={styles.userCard}
        />
        {onToggleCondensed && (
          <Button
            variant="text"
            className={styles.collapseButton}
            startIcon={isCondensed ? <ArrowRight /> : <ArrowLeft />}
            onClick={onToggleCondensed}>
            {isCondensed ? '' : t('Zwiń panel')}
          </Button>
        )}
      </div>
    </Paper>
  );
};

export default TraderClientDashboardMenu;
